'use client'

import { URL_DECOR } from '@/constants/ui'
import StarIcon from '@mui/icons-material/Star'
import { Box, Divider, Stack, Typography } from '@mui/material'
import { useTranslations } from 'next-intl'
import Image from 'next/image'
import { AnimatePresence, motion } from 'framer-motion'
import { useInfiniteReviews, useReviewsSumary } from '../../hooks'
import { StickyBarBottom } from '../../pages/product-detail/components/stickybar-bottom'
import { MAX_ITEMS_REVIEW } from '../../utils/constant'
import { LocalIcon } from '../icon'
import { CheckboxListVoucher } from './checkbox-list-voucher'
import { ReviewListInfinite } from './review-list-infinite'
import { Breadcrumb } from '@/components'
import PanelQuality from '../../pages/product-detail/components/panel-quality'
import SlideProductRelated from './slide-product-related'
import PanelFlashSale from './panel-flash-sale'
import { useProductDetailV2State } from '../../contexts/product-detail'

type ProductInfoV2Props = {
  data: Product

  // dataAffiliateLevel: IAffiliateLevel[] | undefined | null
  // totalAffiliateCustomer: number
}

export const ProductInfoV2 = ({ data }: ProductInfoV2Props) => {
  const t = useTranslations()
  const { productSelected } = useProductDetailV2State()

  const { reviewSummary } = useReviewsSumary(data?.id)
  const reviewAverage = reviewSummary?.avg_rating || 0
  const totalReviews = reviewSummary?.total_review || 0
  const checkIsSale = Boolean(
    (productSelected?.product?.daily_sale && productSelected?.product?.daily_sale?.sale_price) ||
      (data?.daily_sale && data?.daily_sale?.sale_price)
  )
  return (
    <Stack spacing={{ xs: 2, sm: 3, md: 4 }} sx={{ width: '100%' }}>
      {/* Product Information Section */}
      <Stack spacing={2}>
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            gap: '1em',
          }}
        >
          {/* Mô tả sản phẩm ở trên cùng */}
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: '.5em',
            }}
          >
            <Typography variant="body2" sx={{ color: 'five.main', fontWeight: 600 }}>
              {'  '}
              <Box
                component={'span'}
                sx={{
                  verticalAlign: 'middle',
                }}
                color="inherit"
                textTransform={'uppercase'}
              >
                *{t('Top 1 sản phẩm bán chạy')}
              </Box>
            </Typography>
            <Stack mb={0.5} direction="row" alignItems="center" justifyContent="space-between">
              <Typography variant="h1" fontWeight={500} fontSize={{ xs: '1.25em', md: '1.5em' }}>
                {data?.name}
              </Typography>
            </Stack>
            <Stack
              direction={{ xs: 'column', sm: 'row', lg: 'column', xl: 'row' }}
              alignItems={{ xs: 'flex-start', sm: 'center', lg: 'flex-start', xl: 'center' }}
              gap={'8px'}
            >
              <Stack direction={'row'} gap={1}>
                <Stack
                  direction={'row'}
                  spacing={0.5}
                  alignItems="center"
                  p={'2px 8px'}
                  bgcolor="#F46A00"
                  borderRadius="4px"
                >
                  <Stack
                    direction="row"
                    spacing={0.5}
                    alignItems="center"
                    sx={{ fontSize: '1rem', color: '#fff' }}
                  >
                    <Typography fontWeight={600} fontSize="inherit">
                      {reviewAverage ? reviewAverage : 5}
                    </Typography>
                    <StarIcon sx={{ color: '#fff', fontSize: '1rem' }} />
                  </Stack>
                  {totalReviews == 0 && (
                    <Typography
                      sx={{
                        flexGrow: 1,
                        fontSize: '0.875rem',

                        color: '#fff',
                        fontWeight: 400,
                        cursor: 'pointer',
                      }}
                      onClick={() => {
                        const reviewSection = document.getElementById('review')
                        if (reviewSection) {
                          window.scrollTo({
                            top: reviewSection?.offsetTop + 80,
                            behavior: 'smooth',
                          })
                        }
                      }}
                    >
                      Chưa có đánh giá
                    </Typography>
                  )}
                </Stack>
                {data?.sales_qty && (
                  <>
                    <Divider orientation="vertical" flexItem />
                    <Stack
                      direction="row"
                      spacing={0.5}
                      alignItems="center"
                      p={'2px 8px'}
                      border="1px solid #f04438"
                      borderRadius="4px"
                    >
                      <Typography
                        sx={{
                          fontSize: { xs: '0.75rem', md: '1rem' },
                          color: 'five.main',
                          fontWeight: 400,
                        }}
                      >
                        Đã bán
                      </Typography>
                      <Typography
                        fontWeight={700}
                        fontSize={{ xs: '0.875rem', md: '1rem' }}
                        sx={{ color: 'five.main' }}
                      >
                        {data?.sales_qty}
                      </Typography>
                    </Stack>
                  </>
                )}
              </Stack>
            </Stack>
          </Box>
        </Box>
        {/* <ExchangePoints/> */}
        {/* <InputApplyCoupon /> */}
        {/* <CheckboxListVoucher /> */}
        <AnimatePresence mode="wait">
          {checkIsSale && (
            <motion.div
              initial={{ opacity: 0, height: 0, marginTop: 0 }}
              animate={{ opacity: 1, height: 'auto', marginTop: 16 }}
              exit={{ opacity: 0, height: 0, marginTop: 0 }}
              transition={{
                duration: 0.4,
                ease: [0.4, 0, 0.2, 1],
              }}
              style={{ overflow: 'hidden' }}
            >
              <PanelFlashSale data={data} productSelected={productSelected} />
            </motion.div>
          )}
        </AnimatePresence>
        <Box id="sticky-bar-desktop">
          <StickyBarBottom
            hasContainer={false}
            data={data}
            hidePriceSale={checkIsSale}
            wrapBtnProps={{
              direction: { xs: 'column', sm: 'row' },
              sx: {
                '& .main-btn': {
                  minHeight: { xs: '56px', lg: 'unset' },
                },
              },
            }}
            wrapContentProps={{
              sx: {
                border: '0px',
                py: 0,
              },
            }}
            wrapActionBtnProps={{
              sx: { display: { xs: 'none', lg: 'flex' } },
            }}
            wrapListContentProps={{
              sx: {
                '& .discount-item': {
                  display: { xs: 'none', lg: 'flex' },
                },
                '& .image-mobile-product-detail': {
                  display: 'none',
                },
                '& .line-fixed': {
                  display: 'none',
                },
              },
            }}
          />
        </Box>
        <PanelQuality />
        {/* Sản phẩm liên quan */}
        <SlideProductRelated dataDetail={data} />
      </Stack>

      {/* <Divider sx={{ height: '1px', width: '100%', background: '#BBBBBA' }} />
      <SizeGuides /> */}
      {/* <ReviewListInfinite
        data={reviewsData}
        hasNextPage={hasNextPage}
        isFetchingNextPage={isFetchingNextPage}
        fetchNextPage={fetchNextPage}
        isLoading={isLoading}
        isFetching={isFetching}
      /> */}
      {/* <Divider sx={{ height: '1px', width: '100%', background: '#BBBBBA' }} /> */}
      {/* AffiliaterInfo */}
      {/* <AffiliaterInfo dataProduct={data} /> */}
    </Stack>
  )
}
